// On-device camera scanning — the operator is using the ERP ON the phone, so a
// barcode field can open the phone's own camera in place: first successful
// decode fills the field, the overlay closes, the caller runs its existing
// lookup. DISTINCT from phone-pairing (hub/link/bridge — a phone feeding a
// DESKTOP): this component never touches the relay, sessions or sockets; the
// code goes straight to `onCode`, whose consumers STAGE (prefill + lookup) per
// scan gate (c) — committing stays with the operator.
//
// The decode engine is REUSED from ../scanner (decodeFromVideo — the lazy
// zxing-wasm engine); the stream discipline follows
// ScannerApp: getUserMedia({ video: { facingMode: "environment" } }), a
// throttled setTimeout+rAF loop, and the stream stopped on EVERY exit path
// (decode, cancel, Esc/backdrop, unmount).
import { Show, createSignal, onCleanup } from "solid-js";
import { ScanBarcode } from "lucide-solid";
import { Button } from "@erp/ui/button";
import { SegmentedControl } from "@erp/ui/segmented-control";
import { Spinner } from "@erp/ui/spinner";
import { applyContinuousFocus, decodeFromVideo, drawRoiGuide } from "../scanner/index.js";
import type { ScanMode } from "../scanner/index.js";

/** Decode cadence — matches ScannerApp: a camera delivers no frames faster. */
const TICK_MS = 33; // fallback only — rVFC paces the loop where available

/**
 * Can THIS device scan into a field directly? Decided up front, like
 * ScannerApp's `liveSupported` — the button is offered or absent, never
 * offered-then-silently-failing:
 *   • secure context + getUserMedia — outside HTTPS (and on iOS-Safari-HTTP,
 *     where `mediaDevices` is undefined outright) the browser never prompts;
 *   • a COARSE pointer — the feature is for the operator holding the device.
 *     On a desktop the pairing flow (ScanWithPhoneButton) is the camera, and
 *     a webcam pointed back at the operator is not a barcode scanner.
 */
export const canFieldScan = (): boolean =>
  typeof window !== "undefined" &&
  window.isSecureContext &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

export interface FieldScanButtonProps {
  /** Receives the FIRST successfully decoded code. The camera is already stopped and the overlay closed. */
  onCode: (code: string) => void;
  /**
   * The BARCODE position of the dialog's Barcode/QR switch (default "barcode";
   * ISBN fields pass "isbn" for the EAN-13 + Bookland sieve). The switch is
   * always pre-selected to Barcode — the operator flips to QR for printed QR
   * labels, so the engine always runs a single symbol family per frame.
   */
  mode?: ScanMode;
  /** Visible button text. Omitted → icon-only (the aria-label still names it). */
  label?: string;
  class?: string;
  id?: string;
}

/**
 * A "scan with this device's camera" affordance for a barcode/ISBN field.
 * Renders NOTHING when `canFieldScan()` is false, so desktop screens are
 * visually unchanged and the two scan features coexist.
 */
export function FieldScanButton(props: FieldScanButtonProps) {
  if (!canFieldScan()) return null;

  const baseId = props.id ?? "btn-field-scan";
  const [open, setOpen] = createSignal(false);
  const [error, setError] = createSignal("");
  /** First decodable frame seen — until then the overlay says the camera is starting. */
  const [ready, setReady] = createSignal(false);
  // Barcode/QR switch — ALWAYS starts on Barcode (a fresh open never inherits
  // the previous book's flip). Barcode position = the consumer's 1D mode.
  const [scanQr, setScanQr] = createSignal(false);
  const barcodeMode = (): ScanMode => {
    const m = props.mode ?? "barcode";
    return m === "qr" || m === "both" ? "barcode" : m;
  };
  const effectiveMode = (): ScanMode => (scanQr() ? "qr" : barcodeMode());
  let dialog: HTMLDialogElement | undefined;
  let video: HTMLVideoElement | undefined;
  let guide: HTMLCanvasElement | undefined;
  let stream: MediaStream | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** While true no decode tick may run — flipped only once a stream is live. */
  let idle = true;
  /** Bumped on shutdown so a frame-callback still in flight can't revive an old loop. */
  let gen = 0;

  const scheduleTick = (): void => {
    const g = gen;
    const v = video as (HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }) | undefined;
    if (v?.requestVideoFrameCallback) {
      v.requestVideoFrameCallback(() => { if (g === gen) void tick(); });
      return;
    }
    timer = setTimeout(() => requestAnimationFrame(() => { if (g === gen) void tick(); }), TICK_MS);
  };

  /** Stop the camera. EVERY exit path funnels here; safe to call repeatedly. */
  const shutdown = (): void => {
    idle = true;
    gen++;
    setReady(false);
    if (timer) clearTimeout(timer);
    timer = undefined;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    if (video) video.srcObject = null;
    guide?.getContext("2d")?.clearRect(0, 0, guide.width, guide.height);
  };
  onCleanup(shutdown);

  const close = (): void => {
    shutdown();
    setOpen(false);
    if (dialog?.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open"); // jsdom / older engines (Modal's fallback)
    }
  };

  const tick = async (): Promise<void> => {
    if (idle) return;
    if (video && video.videoWidth > 0) {
      if (!ready()) setReady(true);
      if (guide) drawRoiGuide(guide, video, effectiveMode());
      const hit = await decodeFromVideo(video, effectiveMode()).catch(() => null);
      if (idle) return; // closed while this frame was decoding
      const code = hit?.text.trim();
      if (code) {
        // First hit wins: stop the stream and drop the overlay BEFORE the
        // consumer runs, so its lookup never races a second decode.
        close();
        props.onCode(code);
        return;
      }
    }
    scheduleTick();
  };

  const start = async (): Promise<void> => {
    setError("");
    setScanQr(false);
    setOpen(true);
    if (dialog && !dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // Same request as ScannerApp: ideal (not exact) 1080p — without it iOS
        // Safari hands out a low-res stream and 1D decode starves.
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
    } catch {
      // The honest state, not a dead black screen — the overlay stays up with
      // the reason and its close affordances (Cancel, Esc, backdrop).
      setError("Camera access was blocked or no camera is available. Close this and type the code instead.");
      return;
    }
    if (!open()) {
      // Cancelled while the permission prompt was up — release immediately.
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
      return;
    }
    if (video) {
      video.srcObject = stream;
      void video.play().catch(() => undefined);
    }
    void applyContinuousFocus(stream); // best-effort; no-op where unsupported
    idle = false;
    void tick();
  };

  return (
    <>
      <Button
        type="button"
        id={baseId}
        variant="outline"
        class={props.class ?? ""}
        aria-label={props.label ?? "Scan a barcode with the camera"}
        onClick={() => void start()}
      >
        <ScanBarcode size={14} aria-hidden="true" />
        <Show when={props.label}>{props.label}</Show>
      </Button>
      <dialog
        ref={dialog}
        id={`${baseId}-dialog`}
        class="modal"
        aria-label="Scan a barcode with the camera"
        onClose={() => {
          // Native close (Esc, backdrop form method="dialog") — stop the stream.
          if (open()) {
            shutdown();
            setOpen(false);
          }
        }}
      >
        <div class="modal-box shadow-raised w-full max-w-md overflow-hidden rounded-box border border-base-300 bg-base-100 p-0">
          <div class="relative bg-base-300">
            <video ref={video} class="aspect-[3/4] w-full object-cover" muted playsinline />
            <canvas ref={guide} class="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" />
            <Show when={open() && !error() && !ready()}>
              {/* Chrome over the camera frame (DEV-GUIDE-002 §12.1): bg-chrome =
                  transparent-black-700 with white content in both themes — the
                  frame behind it stays visible, and nothing here is a theme
                  stop at an alpha. */}
              <div role="status" class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-chrome text-chrome-content" aria-busy="true">
                <Spinner size="l" inline class="text-chrome-content" />
                <p class="text-xs">Starting the camera…</p>
              </div>
            </Show>
            <Show when={open() && !error()}>
              <div class="absolute inset-x-0 bottom-0 bg-chrome px-4 py-2 text-center text-chrome-content">
                {/* A value pick between two positions is the kit's SegmentedControl
                    (radiogroup, arrow keys), not a `join` of raw buttons. It keeps
                    the ids decisions.md pins: `<id>-mode-barcode` / `<id>-mode-qr`. */}
                <SegmentedControl
                  id={`${baseId}-mode`}
                  aria-label="Code type"
                  value={scanQr() ? "qr" : "barcode"}
                  onChange={(v) => setScanQr(v === "qr")}
                  options={[
                    { label: "Barcode", value: "barcode" },
                    { label: "QR", value: "qr" },
                  ]}
                />
                <p class="mt-1 text-xs">
                  {scanQr()
                    ? "Point the camera at the QR label — the first read fills the field."
                    : "Point the camera at the barcode — the first read fills the field."}
                </p>
              </div>
            </Show>
            <Show when={error()}>
              {/* An error replaces the frame — nothing is behind it worth seeing,
                  so the surface is opaque base-100, not chrome. */}
              <div role="alert" class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-base-100 p-6 text-center">
                <p class="text-sm">{error()}</p>
              </div>
            </Show>
          </div>
          <div class="border-t border-base-300 bg-base-200 px-4 py-3">
            <Button type="button" id={`${baseId}-cancel`} variant="outline" class="w-full" onClick={close}>
              Cancel
            </Button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button aria-label="Close">close</button>
        </form>
      </dialog>
    </>
  );
}
